/**
 * Firm performance overview - a grid of charts on the Manager dashboard, built
 * from RoniDoc usage data (GET /manager/firm-metrics). Theme-aware: currentColor
 * drives axes/grid (the card sets text-muted-foreground), the tooltip reads the
 * theme CSS variables. Data colours are hex (SVG attrs don't resolve var()).
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2, MessageSquare, Clock, FileText, Sparkles } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import apiClient from '@/services/api/ma-client';

const ACCENT = '#35AD88';
const axisTick = { fill: 'currentColor', fontSize: 9 } as any;
const axisLine = { stroke: 'currentColor', opacity: 0.2 } as any;
const tip = {
  contentStyle: { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))', fontSize: 12 },
  labelStyle: { color: 'hsl(var(--foreground))' },
  itemStyle: { color: 'hsl(var(--foreground))' },
  cursor: { fill: 'currentColor', opacity: 0.06 },
} as any;
const legendStyle = { fontSize: 10, color: 'currentColor' } as any;

function Card({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3 text-muted-foreground">
      <p className="px-1 text-sm font-semibold text-foreground">{title}</p>
      {sub && <p className="px-1 text-[11px] text-muted-foreground">{sub}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

const sum = (arr: any[], k: string) => (arr || []).reduce((a, x) => a + (Number(x[k]) || 0), 0);

export default function ManagerMetrics() {
  const { data, isLoading } = useQuery({
    queryKey: ['manager-firm-metrics'],
    queryFn: async () => { const r = await apiClient.get('/manager/firm-metrics'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[var(--v4-accent)]" /></div>;
  if (!data) return null;

  const comm: any[] = data.communications || [];
  const rt: any[] = data.responseTime || [];
  const rtNonZero = rt.filter((r) => r.value > 0);
  const avgResp = rtNonZero.length ? (rtNonZero.reduce((a, r) => a + r.value, 0) / rtNonZero.length) : 0;
  const totalMsgs = sum(comm, 'inbound') + sum(comm, 'outbound');
  const totalDocs = sum(data.documents, 'value');
  const totalAI = sum(data.aiUsage, 'value');
  const DEADLINE_COLORS = ['#ef4444', '#f59e0b', '#22c55e'];

  return (
    <div className="space-y-3">
      <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Firm performance (last 8 weeks)</h2>

      {/* KPI pills */}
      <div className="grid grid-cols-2 gap-2">
        {[
          { icon: MessageSquare, label: 'Communications', value: String(totalMsgs) },
          { icon: Clock, label: 'Avg response', value: avgResp >= 1 ? `${avgResp.toFixed(1)}h` : avgResp > 0 ? `${Math.round(avgResp * 60)}m` : '-' },
          { icon: FileText, label: 'Documents', value: String(totalDocs) },
          { icon: Sparkles, label: 'AI interactions', value: String(totalAI) },
        ].map((k, i) => (
          <div key={i} className="flex items-center gap-2 rounded-xl border border-border bg-card p-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--v4-accent)]/15"><k.icon className="h-4 w-4 text-[var(--v4-accent)]" /></span>
            <div className="min-w-0">
              <p className="text-base font-bold leading-none text-foreground">{k.value}</p>
              <p className="truncate text-[10px] text-muted-foreground">{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Communications in vs out */}
      <Card title="Communications" sub="Messages received vs sent, per week">
        <ResponsiveContainer width="99%" height={150}>
          <BarChart data={comm} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.12} />
            <XAxis dataKey="name" tick={axisTick} axisLine={axisLine} tickLine={axisLine} />
            <YAxis tick={axisTick} axisLine={axisLine} tickLine={axisLine} width={24} />
            <Tooltip {...tip} />
            <Legend verticalAlign="bottom" wrapperStyle={legendStyle} />
            <Bar name="Received" dataKey="inbound" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            <Bar name="Sent" dataKey="outbound" fill={ACCENT} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Avg response time */}
      <Card title="Average response time" sub="Hours to reply to a client message">
        <ResponsiveContainer width="99%" height={140}>
          <AreaChart data={rt}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.12} />
            <XAxis dataKey="name" tick={axisTick} axisLine={axisLine} tickLine={axisLine} />
            <YAxis tick={axisTick} axisLine={axisLine} tickLine={axisLine} width={28} tickFormatter={(v) => `${v}h`} />
            <Tooltip {...tip} formatter={(v: number) => [`${v}h`, 'Avg response']} />
            <Area type="monotone" dataKey="value" stroke={ACCENT} fill={ACCENT} fillOpacity={0.18} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Documents generated */}
      <Card title="Documents generated" sub="Created in RoniDoc, per week">
        <ResponsiveContainer width="99%" height={140}>
          <BarChart data={data.documents}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.12} />
            <XAxis dataKey="name" tick={axisTick} axisLine={axisLine} tickLine={axisLine} />
            <YAxis tick={axisTick} axisLine={axisLine} tickLine={axisLine} width={24} allowDecimals={false} />
            <Tooltip {...tip} />
            <Bar dataKey="value" name="Documents" fill="#6366f1" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* RoniDoc usage */}
      <Card title="RoniDoc usage" sub="AI chats & searches by the team, per week">
        <ResponsiveContainer width="99%" height={140}>
          <LineChart data={data.aiUsage}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.12} />
            <XAxis dataKey="name" tick={axisTick} axisLine={axisLine} tickLine={axisLine} />
            <YAxis tick={axisTick} axisLine={axisLine} tickLine={axisLine} width={24} allowDecimals={false} />
            <Tooltip {...tip} />
            <Line type="monotone" dataKey="value" name="Interactions" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Active clients */}
      <Card title="Active clients" sub="Clients communicating each week">
        <ResponsiveContainer width="99%" height={140}>
          <AreaChart data={data.activeClients}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.12} />
            <XAxis dataKey="name" tick={axisTick} axisLine={axisLine} tickLine={axisLine} />
            <YAxis tick={axisTick} axisLine={axisLine} tickLine={axisLine} width={24} allowDecimals={false} />
            <Tooltip {...tip} />
            <Area type="monotone" dataKey="value" name="Active clients" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.18} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Top clients by volume */}
      {data.topClients?.length > 0 && (
        <Card title="Most active clients" sub="By messages, last 90 days">
          <ResponsiveContainer width="99%" height={Math.max(120, data.topClients.length * 26)}>
            <BarChart data={data.topClients} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.12} />
              <XAxis type="number" tick={axisTick} axisLine={axisLine} tickLine={axisLine} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={axisTick} axisLine={axisLine} tickLine={axisLine} width={96} />
              <Tooltip {...tip} />
              <Bar dataKey="value" name="Messages" fill={ACCENT} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Deadlines (delays) */}
      <Card title="Compliance deadlines" sub="Overdue, due soon & upcoming">
        <ResponsiveContainer width="99%" height={170}>
          <PieChart>
            <Pie data={data.deadlines} dataKey="value" nameKey="name" cx="50%" cy="46%" outerRadius={58} innerRadius={34}>
              {(data.deadlines || []).map((_: any, i: number) => <Cell key={i} fill={DEADLINE_COLORS[i % DEADLINE_COLORS.length]} />)}
            </Pie>
            <Tooltip {...tip} />
            <Legend verticalAlign="bottom" wrapperStyle={legendStyle} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
